/**
 * /earthworks/quoted-vs-actual/:jobType — one job type's quoted vs actual
 * (EW-8, GET /earthworks/quoted-vs-actual/job-types/:jobType).
 *
 * The totals, variances and rates are the API's. Below 5 completed jobs with a
 * quote there are variances only. From 5, an office user can ask for
 * suggestions; each is a DRAFT the office accepts or dismisses, and accepting
 * changes the default the NEXT AI quote uses — never a past quote, ticket or
 * invoice. The explanation is written by AI from the API's figures and is
 * thrown away by the API if it states any other number.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Bot, Check, Scale, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useAcceptEwQvaSuggestion,
  useDismissEwQvaSuggestion,
  useEwQvaJobType,
  useEwQvaNarrative,
  useGenerateEwQvaSuggestions,
} from '../../../hooks/useEwQva';
import { canAdminEw, canReadEwRates, ewApiError, ewRefusal } from '../../../lib/earthworks';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { formatInstantDay } from '../../../lib/calendar-date';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import type { EwQvaDefault, EwQvaJobType, EwQvaSuggestion, EwQvaTypeMetric } from '../../../types/ewQva';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList, useOpenRecord } from '../../downhole/recordPage';
import { EW_EYEBROW, EW_PRIMARY, EW_SECONDARY, EwDialog, EwEmpty, EwFormError, EwLoading, EwPanel, EwRegister, isNotFound, type EwColumn } from '../EwBits';
import { QvaPairStat, VariancePct, fmtMetric, fmtRate, volumeWord } from './EwQvaBits';

type MetricRow = EwQvaTypeMetric & { id: string };
type JobRow = EwQvaJobType['jobs'][number];

const fmtDefault = (value: number, unit: string | null) =>
  unit === 'm3' ? `${value} m³` : unit === 'yd3' ? `${value} yd³` : `× ${value}`;

export default function EwQvaJobTypeScreen() {
  const { jobType = '' } = useParams<{ jobType: string }>();
  const back = useBackToList('/earthworks/quoted-vs-actual');
  const openRecord = useOpenRecord();
  const tz = useTenantTimeZone();
  const { user } = useAuth();
  const canRead = canReadEwRates(user?.role);
  const canDecide = canAdminEw(user?.role);
  const query = useEwQvaJobType(jobType, canRead);
  const generate = useGenerateEwQvaSuggestions(jobType);
  const accept = useAcceptEwQvaSuggestion(jobType);
  const dismiss = useDismissEwQvaSuggestion(jobType);
  const narrative = useEwQvaNarrative(jobType);
  const [confirming, setConfirming] = useState<EwQvaSuggestion | null>(null);
  const [decideError, setDecideError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [narrativeError, setNarrativeError] = useState<string | null>(null);

  const d = query.data ?? null;
  const cur = d?.currency ?? null;
  const vol = d ? volumeWord(d.unitSystem) : 'm³';
  const drafts = (d?.suggestions ?? []).filter((s) => s.status === 'draft');
  const decided = (d?.suggestions ?? []).filter((s) => s.status !== 'draft');
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));

  const doGenerate = () => {
    setGenerateError(null);
    generate.mutate(undefined, {
      onSuccess: (r) => {
        const n = r?.suggestions.length ?? 0;
        toast.success(n ? `${n} suggestion${n === 1 ? '' : 's'} to review` : 'No new suggestion from these figures');
      },
      onError: (err) => setGenerateError(ewRefusal(err, 'Suggestions could not be worked out.')),
    });
  };
  const doAccept = () => {
    if (!confirming) return;
    setDecideError(null);
    accept.mutate(confirming.id, {
      onSuccess: (r) => {
        toast.success(r?.note ?? 'Default updated for the next AI quote');
        setConfirming(null);
      },
      onError: (err) => setDecideError(ewRefusal(err, 'The suggestion could not be accepted.')),
    });
  };
  const doDismiss = (s: EwQvaSuggestion) => {
    setDecideError(null);
    dismiss.mutate(s.id, {
      onSuccess: () => toast.success('Suggestion dismissed'),
      onError: (err) => setDecideError(ewRefusal(err, 'The suggestion could not be dismissed.')),
    });
  };
  const doNarrative = () => {
    setNarrativeError(null);
    narrative.mutate(undefined, {
      onError: (err) => {
        const e = ewApiError(err);
        setNarrativeError(e.status === 402 ? (e.message ?? 'The AI budget for this month is used up.') : ewRefusal(err, 'The explanation could not be written.'));
      },
    });
  };

  const metricColumns: EwColumn<MetricRow>[] = [
    { key: 'label', header: 'Figure', render: (m) => <span className="font-bold text-white">{m.label}</span> },
    { key: 'jobs', header: 'Jobs', align: 'right', render: (m) => String(m.jobs) },
    { key: 'quoted', header: 'Quoted', align: 'right', render: (m) => fmtMetric(m.quotedTotal, m.unit, cur) },
    { key: 'actual', header: 'Actual', align: 'right', render: (m) => fmtMetric(m.actualTotal, m.unit, cur) },
    { key: 'pct', header: 'Variance', align: 'right', render: (m) => <VariancePct pct={m.variancePct} testId={`ew-qva-type-pct-${m.key}`} /> },
    { key: 'perJob', header: 'Per job (quoted → actual)', align: 'right', render: (m) => `${fmtMetric(m.quotedPerJob, m.unit, cur)} → ${fmtMetric(m.actualPerJob, m.unit, cur)}` },
  ];
  const jobColumns: EwColumn<JobRow>[] = [
    { key: 'job', header: 'Job', render: (j) => (<><p className="font-bold text-white">{j.jobNumber}</p><p className="text-xs text-fieldiq-muted">{j.customerName ?? '—'} · {j.siteName ?? '—'}</p></>) },
    { key: 'completed', header: 'Completed', render: (j) => formatInstantDay(j.completedAt, tz) ?? '—' },
    { key: 'machine', header: 'Machine hours', align: 'right', render: (j) => <VariancePct pct={j.metrics.find((m) => m.key === 'machine_hours')?.variancePct} /> },
    { key: 'loads', header: 'Loads', align: 'right', render: (j) => <VariancePct pct={j.metrics.find((m) => m.key === 'loads')?.variancePct} /> },
    { key: 'crew', header: 'Crew hours', align: 'right', render: (j) => <VariancePct pct={j.metrics.find((m) => m.key === 'crew_hours')?.variancePct} /> },
    { key: 'billed', header: 'Billed', align: 'right', render: (j) => <VariancePct pct={j.metrics.find((m) => m.key === 'billed')?.variancePct} /> },
  ];

  return (
    <div data-testid="ew-qva-type-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`${EW_EYEBROW} · Quoted vs actual`}
        title={d?.label ?? (query.isLoading ? 'Job type' : '—')}
        description={d ? `${d.quotedCompletedJobs} completed job${d.quotedCompletedJobs === 1 ? '' : 's'} with a quote, of ${d.completedJobs} completed.` : undefined}
        onBack={back}
        backLabel="Back"
        stats={[
          { icon: <Scale className="size-4" aria-hidden="true" />, label: 'With a quote', tone: 'neutral', loading: query.isLoading, value: d ? (d.thresholdMet ? String(d.quotedCompletedJobs) : `${d.quotedCompletedJobs} of ${d.minimumJobs}`) : undefined },
          { icon: <Sparkles className="size-4" aria-hidden="true" />, label: 'To review', tone: 'accent', loading: query.isLoading, value: d ? String(drafts.length) : undefined },
        ]}
        actions={
          d && canDecide && d.hasAiQuote && d.thresholdMet ? (
            <button type="button" className={DH_BAND_SECONDARY} onClick={doGenerate} disabled={generate.isPending} data-testid="ew-qva-generate">
              <Sparkles className="size-4" aria-hidden="true" />
              {generate.isPending ? 'Working out…' : 'Check for suggestions'}
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canRead ? (
            <EwPanel eyebrow="Quoted vs actual" title="Not available">
              <EwEmpty>Quoted vs actual compares quotes and prices, so it is for admins, office staff, supervisors and dispatchers.</EwEmpty>
            </EwPanel>
          ) : query.isLoading ? (
            <EwLoading label="Loading quoted vs actual" />
          ) : notFound ? (
            <EwPanel eyebrow="Quoted vs actual" title="Not found"><EwEmpty>There is no such job type.</EwEmpty></EwPanel>
          ) : failure ? (
            <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="quoted vs actual" />
          ) : d ? (
            <>
              <EwFormError message={generateError} />
              {d.thresholdNote && (
                <p data-testid="ew-qva-threshold" className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-muted">{d.thresholdNote}</p>
              )}
              {!d.hasAiQuote && (
                <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-muted">No AI quote prices {d.label.toLowerCase()} jobs, so there is no default to suggest. The variances are shown.</p>
              )}

              {d.hasAiQuote && (
                <EwPanel eyebrow="Suggestions" title="For the AI-quote defaults" testId="ew-qva-suggestions"
                  footer="Accepting changes the next AI quote for this job type only. No quote, ticket or invoice already made changes.">
                  <div className="space-y-3 p-4">
                    {/* An accept refusal shows in the open dialog; only a dismiss refusal shows here. */}
                    <EwFormError message={confirming ? null : decideError} />
                    {drafts.length === 0 ? (
                      <EwEmpty>{d.thresholdMet ? 'No suggestion waiting. “Check for suggestions” works one out from the figures below.' : 'Suggestions start at 5 completed jobs with a quote.'}</EwEmpty>
                    ) : drafts.map((s) => (
                      <div key={s.id} data-testid="ew-qva-suggestion" className="rounded-fi-panel border border-fi-amber/50 bg-fi-amber/5 px-4 py-3">
                        <p className="text-sm text-fieldiq-text">{s.message}</p>
                        <p className="mt-1 text-xs text-fieldiq-muted">
                          {s.paramLabel}: {fmtDefault(s.currentValue, s.unit)} → <span className="font-bold text-fieldiq-text">{fmtDefault(s.suggestedValue, s.unit)}</span> · from {s.jobsCount} jobs
                        </p>
                        {canDecide && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button type="button" className={EW_PRIMARY} onClick={() => { setDecideError(null); setConfirming(s); }} data-testid="ew-qva-accept">
                              <Check className="size-4" aria-hidden="true" /> Accept
                            </button>
                            <button type="button" className={EW_SECONDARY} onClick={() => doDismiss(s)} disabled={dismiss.isPending} data-testid="ew-qva-dismiss">
                              <X className="size-4" aria-hidden="true" /> Dismiss
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {decided.length > 0 && (
                      <ul className="space-y-1 border-t border-fieldiq-border pt-3 text-xs text-fieldiq-muted" data-testid="ew-qva-decided">
                        {decided.slice(0, 10).map((s) => (
                          <li key={s.id}>
                            <span className={s.status === 'accepted' ? 'font-bold text-fi-green' : ''}>{s.status === 'accepted' ? 'Accepted' : 'Dismissed'}</span>
                            {' '}{s.paramLabel} {fmtDefault(s.currentValue, s.unit)} → {fmtDefault(s.suggestedValue, s.unit)}
                            {s.decidedByName ? ` · ${s.decidedByName}` : ''}{s.decidedAt ? ` · ${formatInstantDay(s.decidedAt, tz) ?? ''}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </EwPanel>
              )}

              {d.defaults.length > 0 && (
                <EwPanel eyebrow="AI quote" title="Defaults the next draft uses" testId="ew-qva-defaults">
                  <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
                    {d.defaults.map((x: EwQvaDefault) => (
                      <div key={x.param} data-testid={`ew-qva-default-${x.param}`} className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3">
                        <p className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{x.label}</p>
                        <p className="mt-1 font-mono text-lg font-bold text-white">{fmtDefault(x.value, x.unit)}</p>
                        <p className="text-xs text-fieldiq-muted">
                          {x.source === 'tenant' ? `Your default${x.setAt ? `, set ${formatInstantDay(x.setAt, tz) ?? ''}` : ''} · planning default ${fmtDefault(x.codeDefault, x.unit)}` : 'Planning default'}
                        </p>
                      </div>
                    ))}
                  </div>
                </EwPanel>
              )}

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="ew-qva-type-rates">
                <QvaPairStat label={`Machine hours per ${vol}`} quoted={fmtRate(d.rates.machineHoursPerVolume.quoted, `h/${vol}`)} actual={fmtRate(d.rates.machineHoursPerVolume.actual, `h/${vol}`)} />
                <QvaPairStat label={`Loads per ${vol}`} quoted={fmtRate(d.rates.loadsPerVolume.quoted, `/${vol}`)} actual={fmtRate(d.rates.loadsPerVolume.actual, `/${vol}`)} />
                <QvaPairStat label="Machine hours per job" quoted={fmtMetric(d.rates.machineHoursPerJob.quoted, 'hour', cur)} actual={fmtMetric(d.rates.machineHoursPerJob.actual, 'hour', cur)} />
                <QvaPairStat label="Crew hours per job" quoted={fmtMetric(d.rates.crewHoursPerJob.quoted, 'hour', cur)} actual={fmtMetric(d.rates.crewHoursPerJob.actual, 'hour', cur)} />
              </div>

              <EwPanel eyebrow="Quoted vs actual" title="Totals across the completed jobs" testId="ew-qva-type-metrics">
                <EwRegister
                  query={query}
                  rows={d.metrics.map((m) => ({ ...m, id: m.key }))}
                  columns={metricColumns}
                  rail={(m) => ((m.variancePct ?? 0) > 0 ? 'border-l-fi-amber' : 'border-l-transparent')}
                  subject="totals"
                  testId="ew-qva-type-metric"
                  minWidth="min-w-[880px]"
                  empty={<EwEmpty>No completed {d.label.toLowerCase()} jobs with a quote yet.</EwEmpty>}
                  card={(m) => (
                    <>
                      <div className="flex items-start justify-between gap-3"><p className="text-sm font-bold text-white">{m.label}</p><VariancePct pct={m.variancePct} /></div>
                      <p className="mt-1 font-mono text-sm">{fmtMetric(m.quotedTotal, m.unit, cur)} → {fmtMetric(m.actualTotal, m.unit, cur)}</p>
                    </>
                  )}
                />
              </EwPanel>

              <EwPanel
                eyebrow="Explanation"
                title="In words"
                testId="ew-qva-narrative"
                actions={canDecide && d.quotedCompletedJobs > 0 ? (
                  <button type="button" className={EW_SECONDARY} onClick={doNarrative} disabled={narrative.isPending} data-testid="ew-qva-explain">
                    <Bot className="size-4" aria-hidden="true" /> {narrative.isPending ? 'Writing…' : d.narrative ? 'Write again' : 'Explain with AI'}
                  </button>
                ) : undefined}
              >
                <div className="space-y-2 p-4">
                  <EwFormError message={narrativeError} />
                  {d.narrative ? (
                    <>
                      <p className="text-sm text-fieldiq-text" data-testid="ew-qva-narrative-text">{d.narrative.text}</p>
                      <p className="text-xs text-fieldiq-muted">Written by AI from the figures above{d.narrative.createdAt ? ` · ${formatInstantDay(d.narrative.createdAt, tz) ?? ''}` : ''}. The figures are the record.</p>
                    </>
                  ) : (
                    <EwEmpty>No explanation yet. The figures above are the record; the AI only puts them into words.</EwEmpty>
                  )}
                </div>
              </EwPanel>

              <EwPanel eyebrow="Jobs" title={`Completed ${d.label.toLowerCase()} jobs`} testId="ew-qva-type-jobs"
                footer={d.jobs.length ? `${d.jobs.length} job${d.jobs.length === 1 ? '' : 's'}` : undefined}>
                <EwRegister
                  query={query}
                  rows={d.jobs}
                  columns={jobColumns}
                  rail={(j) => (j.hasQuote ? 'border-l-transparent' : 'border-l-fi-steel')}
                  onOpen={(j) => openRecord(`/earthworks/jobs/${j.id}?tab=quoted-vs-actual`)}
                  subject="jobs"
                  testId="ew-qva-type-job"
                  minWidth="min-w-[900px]"
                  empty={<EwEmpty>No completed {d.label.toLowerCase()} jobs yet.</EwEmpty>}
                  card={(j) => (
                    <>
                      <p className="text-sm font-bold text-white">{j.jobNumber}</p>
                      <p className="text-xs text-fieldiq-muted">{j.customerName ?? '—'} · {formatInstantDay(j.completedAt, tz) ?? '—'}</p>
                      <p className="mt-1 text-xs">Machine hours <VariancePct pct={j.metrics.find((m) => m.key === 'machine_hours')?.variancePct} /></p>
                    </>
                  )}
                />
              </EwPanel>
            </>
          ) : null}
        </div>
      </section>

      <EwDialog
        open={!!confirming}
        title="Accept this suggestion?"
        onClose={() => setConfirming(null)}
        footer={
          <>
            <button type="button" className={EW_SECONDARY} onClick={() => setConfirming(null)} disabled={accept.isPending}>Not now</button>
            <button type="button" className={EW_PRIMARY} onClick={doAccept} disabled={accept.isPending} data-testid="ew-qva-accept-confirm">
              {accept.isPending ? 'Saving…' : 'Accept'}
            </button>
          </>
        }
      >
        {confirming && (
          <div className="space-y-2 text-sm text-fieldiq-text">
            <p>{confirming.paramLabel}: {fmtDefault(confirming.currentValue, confirming.unit)} → <span className="font-bold">{fmtDefault(confirming.suggestedValue, confirming.unit)}</span></p>
            <p className="text-fieldiq-muted">The next AI quote for {d?.label.toLowerCase() ?? 'this job type'} jobs uses the new value. Quotes, tickets and invoices already made do not change.</p>
            <EwFormError message={decideError} />
          </div>
        )}
      </EwDialog>
    </div>
  );
}
